<template>
  <span
    class="bui-tbl-chk"
    :class="[`state-${effectiveState}`, { interactive: !disabled }]"
    role="checkbox"
    :aria-checked="ariaChecked"
    :tabindex="disabled ? -1 : 0"
    @click="onClick"
    @keydown.space.prevent="onClick"
    @keydown.enter.prevent="onClick"
  >
    <span class="fill" />
    <span class="fill-down" />
    <span v-if="showCheckMark" class="check" aria-hidden="true">
      <svg viewBox="0 0 10 8" focusable="false">
        <path
          d="M1 4 L4 7 L9 1"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
    <span v-if="indeterminate" class="dash" aria-hidden="true">
      <svg viewBox="0 0 10 2" focusable="false">
        <path
          d="M0.5 1 H9.5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
    </span>
  </span>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

/**
 * Visual states sourced from Figma `chkbox` (619:7014):
 *   unchkd, chkd, unchkd-hov, chkd-delay, unchkd-dn, chkd-dn, chckd-status.
 *
 * Most callers only need `model-value` (boolean) + optional `indeterminate`;
 * the `state` prop is exposed for the rare case where a parent needs to
 * force-pin a press / hover state.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    indeterminate?: boolean;
    disabled?: boolean;
    /** Force-state. Overrides modelValue/indeterminate for visuals only. */
    state?:
      | 'unchkd'
      | 'chkd'
      | 'unchkd-hov'
      | 'chkd-delay'
      | 'unchkd-dn'
      | 'chkd-dn'
      | 'chckd-status';
  }>(),
  { modelValue: false, indeterminate: false, disabled: false },
);

const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>();

const effectiveState = computed(() => {
  if (props.state) return props.state;
  if (props.indeterminate) return 'chckd-status';
  return props.modelValue ? 'chkd' : 'unchkd';
});

const showCheckMark = computed(
  () => ['chkd', 'chkd-down', 'chkd-delay'].includes(effectiveState.value),
);

const ariaChecked = computed<'mixed' | boolean>(() => {
  if (props.indeterminate) return 'mixed';
  return !!props.modelValue;
});

function onClick(e: MouseEvent | KeyboardEvent) {
  if (props.disabled) return;
  e.stopPropagation();
  emit('update:modelValue', !props.modelValue);
}
</script>

<style lang="scss" scoped>
.bui-tbl-chk {
  position: relative;
  display: inline-block;
  width: var(--bui-chk-size, 20px);
  height: var(--bui-chk-size, 20px);
  border-radius: var(--bui-chk-radius, 4px);
  flex-shrink: 0;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &.interactive {
    cursor: pointer;
  }

  .fill,
  .fill-down {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-sizing: border-box;
    transition:
      background-color 120ms ease,
      border-color 120ms ease,
      opacity 120ms ease;
  }

  .fill {
    background: var(--bui-chk-fill, #fffcfd);
    border: 1px solid var(--bui-chk-border, rgba(193, 195, 218, 0.48));
  }

  .fill-down {
    background: var(--bui-chk-down-fill, #6b1ab1);
    border: 1px solid var(--bui-chk-down-border, #4b1886);
    opacity: 0;
  }

  .check,
  .dash {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--bui-chk-fill, #fffcfd);

    svg {
      width: 60%;
      height: auto;
      display: block;
    }
  }

  // States
  &.state-unchkd-hov .fill {
    border-color: var(--bui-chk-checked-fill, #de3da9);
  }

  &.state-chkd .fill {
    background: var(--bui-chk-checked-fill, #de3da9);
    border-color: var(--bui-chk-checked-fill, #de3da9);
  }

  &.state-chkd-delay .fill {
    background: var(--bui-chk-checked-fill, #de3da9);
    border-color: var(--bui-chk-checked-fill, #de3da9);
    opacity: 0.6;
  }

  &.state-unchkd-dn .fill-down {
    opacity: 1;
  }

  &.state-chkd-dn {
    .fill {
      background: var(--bui-chk-checked-fill, #de3da9);
      border-color: var(--bui-chk-checked-fill, #de3da9);
    }
    .fill-down {
      opacity: 1;
    }
  }

  &.state-chckd-status .fill {
    background: var(--bui-chk-checked-fill, #de3da9);
    border-color: var(--bui-chk-checked-fill, #de3da9);
  }

  &:hover.interactive:not(.state-chkd):not(.state-chkd-dn):not(.state-chckd-status) {
    .fill {
      border-color: var(--bui-chk-checked-fill, #de3da9);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--bui-chk-checked-fill, #de3da9);
    outline-offset: 2px;
  }
}
</style>
